import * as htmlToImage from 'html-to-image';
export const formatLangCode = (langCode) => langCode.split('-')[0];
export const generateIdRoutePath = (route, id) => {
    return route.replace(':id', id);
};
export const generateTaskRoutePath = (route, id, task) => {
    return route.replace(':id', id).replace(':task', String(task));
};
export const generateSessionRoutePath = (route, id, session) => {
    return route.replace(':id', id).replace(':session', session);
};
export const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};
export const formatSeconds = (totalSeconds) => {
    if (totalSeconds < 0) {
        return 'Invalid Input';
    }
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (num) => String(num).padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};
export const getAtomFormattedDate = (timestamp) => {
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    const pad = (num, size = 2) => String(num).padStart(size, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    const milliseconds = pad(date.getMilliseconds(), 3);
    const tzOffset = date.getTimezoneOffset();
    const tzSign = tzOffset <= 0 ? '+' : '-';
    const tzHours = pad(Math.floor(Math.abs(tzOffset) / 60));
    const tzMinutes = pad(Math.abs(tzOffset) % 60);
    const timezone = `${tzSign}${tzHours}:${tzMinutes}`;
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${timezone}`;
};
export const parseAtomFormatDate = ({ dateString, atLabel, clockLabel, locale, timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone, }) => {
    const date = new Date(dateString);
    const day = date.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone,
    });
    const time = date.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: locale === 'en',
        timeZone,
    });
    return {
        __html: `${day} <span>${atLabel}</span> ${time} ${locale === 'en' ? '' : `<span>${clockLabel}</span>`}`,
    };
};
export const getMediaType = (base64) => {
    if (base64.includes('data:video')) {
        return 'video';
    }
    else if (base64.includes('data:image')) {
        return 'image';
    }
    else {
        return 'unknown';
    }
};
export function getDistance(userPosition, targetLocation) {
    const earthRadius = 6371000; // in meters
    const toRadians = (degrees) => degrees * (Math.PI / 180);
    const lat1 = toRadians(userPosition.lat);
    const lon1 = toRadians(userPosition.lng);
    const lat2 = toRadians(targetLocation.lat);
    const lon2 = toRadians(targetLocation.lng);
    const latDelta = lat2 - lat1;
    const lonDelta = lon2 - lon1;
    const a = Math.sin(latDelta / 2) * Math.sin(latDelta / 2) +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(lonDelta / 2) * Math.sin(lonDelta / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadius * c;
}
export const setViewportHeight = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
};
export const parseHtml = (htmlString) => {
    if (!htmlString) {
        return '';
    }
    // 1. Decode Unicode escapes like \u003Cp\u003E => <p>
    const decodeUnicodeEscapes = (str) => str.replace(/\\u([\dA-Fa-f]{4})/g, (_, g1) => String.fromCharCode(parseInt(g1, 16)));
    let text = decodeUnicodeEscapes(htmlString);
    // 2. Replace block/linebreak tags with newlines
    text = text.replace(/<\s*br\s*\/?>/gi, '\n').replace(/<\s*\/?(div|p)\s*[^>]*>/gi, '\n');
    // 3. Remove all other HTML tags
    text = text.replace(/<[^>]+>/g, '');
    // 4. Decode HTML entities like &nbsp;
    const tempElement = document.createElement('textarea');
    tempElement.innerHTML = text;
    const decodedText = tempElement.value;
    // 5. Remove leading empty lines ONLY — preserve all trailing spaces and line endings
    return decodedText.replace(/^\s*\n/, '');
};
export const getFileType = (url) => {
    const extension = url.split('.').pop()?.toLowerCase();
    if (!extension) {
        return 'unknown';
    }
    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    const videoExts = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv'];
    const audioExts = ['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a', 'amr', '3gp'];
    if (imageExts.includes(extension)) {
        return 'image';
    }
    if (videoExts.includes(extension)) {
        return 'video';
    }
    if (audioExts.includes(extension)) {
        return 'audio';
    }
    return 'unknown';
};
export const detectUserAgentInfo = () => {
    const ua = navigator.userAgent;
    let browser = 'Unknown';
    let browserVersion = 'Unknown';
    let os = 'Unknown';
    let osVersion = 'Unknown';
    // ----------- BROWSER DETECTION -----------
    const browserRegexes = [
        { name: 'Edge', regex: /Edg\/([\d.]+)/ },
        { name: 'Opera', regex: /OPR\/([\d.]+)/ },
        { name: 'Chrome', regex: /Chrome\/([\d.]+)/ },
        { name: 'Firefox', regex: /Firefox\/([\d.]+)/ },
        { name: 'Safari', regex: /Version\/([\d.]+).*Safari/ },
        { name: 'Internet Explorer', regex: /MSIE ([\d.]+);/ },
        { name: 'Internet Explorer', regex: /Trident.*rv:([\d.]+)/ },
    ];
    for (const { name, regex } of browserRegexes) {
        const match = ua.match(regex);
        if (match) {
            browser = name;
            browserVersion = match[1];
            break;
        }
    }
    // ----------- OS DETECTION -----------
    if (/Windows NT 10.0/.test(ua)) {
        os = 'Windows';
        osVersion = '10';
    }
    else if (/Windows NT 6.3/.test(ua)) {
        os = 'Windows';
        osVersion = '8.1';
    }
    else if (/Windows NT 6.2/.test(ua)) {
        os = 'Windows';
        osVersion = '8';
    }
    else if (/Windows NT 6.1/.test(ua)) {
        os = 'Windows';
        osVersion = '7';
    }
    else if (/Mac OS X (\d+[_\.]\d+[_\.]?\d*)/.test(ua)) {
        os = 'macOS';
        osVersion = ua.match(/Mac OS X (\d+[_\.]\d+[_\.]?\d*)/)[1].replace(/_/g, '.');
    }
    else if (/Android (\d+[\.\d]*)/.test(ua)) {
        os = 'Android';
        osVersion = ua.match(/Android (\d+[\.\d]*)/)[1];
    }
    else if (/iPhone OS (\d+[_\d]*)/.test(ua)) {
        os = 'iOS';
        osVersion = ua.match(/iPhone OS (\d+[_\d]*)/)[1].replace(/_/g, '.');
    }
    else if (/iPad.*OS (\d+[_\d]*)/.test(ua)) {
        os = 'iOS';
        osVersion = ua.match(/iPad.*OS (\d+[_\d]*)/)[1].replace(/_/g, '.');
    }
    else if (/Linux/.test(ua)) {
        os = 'Linux';
    }
    return { browser, browserVersion, os, osVersion };
};
export const takeScreenshot = async ({ element, options, }) => {
    return await htmlToImage.toPng(element, {
        ...options,
        pixelRatio: 1,
        filter: (node) => {
            if (node instanceof HTMLElement) {
                if (node.matches('.on-print-hidden')) {
                    return false;
                }
            }
            return true;
        },
    });
};
export const triggerDownload = ({ file, filename }) => {
    const link = document.createElement('a');
    link.href = file;
    link.download = filename;
    link.click();
};
export const copyTextToClipboard = async ({ textToCopy, setJustCopied, }) => {
    try {
        await navigator.clipboard.writeText(textToCopy);
        setJustCopied(true);
        setTimeout(() => setJustCopied(false), 1500);
    }
    catch (err) {
        console.error(err);
    }
};
